/**
 * Deterministic world generation.
 *
 * The server generates the map, then sends *only the 32-bit seed* to clients,
 * which regenerate a byte-identical terrain array locally. Map cost on the
 * wire: 4 bytes.
 */

import { MAP_W, MAP_H, TILE_COUNT, Terrain, USABLE_LAND, WALKABLE } from './constants';
import { fbm, hash2, mulberry32, smoothstep } from './util';

export interface WorldMap {
  seed: number;
  terrain: Uint8Array;
  /**
   * 1 where a river runs.
   *
   * Rivers are shallow water like any other, so everything that already knows
   * "shallows are not land" keeps working — they cannot be built on, spawned in
   * or walled, and a soldier wades across them at half speed. What this mask
   * adds is the one rule terrain alone could not express: **a river belongs to
   * nobody.** Coastal shelf is claimable from the beach beside it, and without
   * the mask a one-tile stream would simply be painted over by whoever owned
   * both banks, which is not a river, it is a puddle in somebody's province.
   *
   * Derived from the seed like everything else, so client and server agree
   * without a byte on the wire.
   */
  river: Uint8Array;
  landTiles: number;
  /** the played rectangle; everything outside it is deep ocean */
  worldW: number;
  worldH: number;
}

/**
 * Feature scale.
 *
 * Every frequency is divided by how much bigger the world is than the original
 * 384 x 240 continent, so enlarging the world grows the **same** landmass at a
 * higher resolution instead of chopping it into an archipelago. That matters:
 * bots march, they do not sail, and a map of islands is a map where the war
 * stops at whatever coastline the biggest empire reaches.
 *
 * Because it keys off the *world* size and not the grid, a small world and a
 * large one are recognisably the same continent at different scales rather than
 * two different planets.
 */
/**
 * **The sea level is a quantile, not a number.**
 *
 * It used to be a fixed elevation cut, which meant the amount of land in a world
 * was whatever that seed's noise happened to produce: most maps were fine and
 * some came out as an archipelago with nowhere for a hundred kingdoms to stand.
 * Measuring the elevation field first and then flooding it to exactly this share
 * makes every world a continent — the *shape* still varies wildly from seed to
 * seed, only the amount of it is guaranteed. Land always outnumbers water.
 */
/*
 * Down from 0.56, because a world that is almost all land is a world with
 * nothing in it: no bays to defend, no crossings to hold, nowhere a fleet is
 * worth building. Rivers (below) then take a little more back out of the middle.
 * The floor is the promise that land always outnumbers water — at 0.52 the
 * driest seed measured comes out around 52 % claimable, which keeps it with room
 * to spare rather than by a rounding error.
 */
/*
 * Raised a little once High Peaks arrived.
 *
 * This is the share of the world that comes out as *land*; the promise players
 * were given is about **claimable** land, and the spine of a range is now
 * neither. So the quantile has to sit above the promise by whatever the peaks
 * eat.
 *
 * Raising it far is the wrong lever and was tried first: more land means a
 * lower waterline, a lower waterline means more ground above `MOUNTAIN_RISE`,
 * and more mountain means more peak — pushing this to 0.63 made the claimable
 * share go *down*. The fix was to seal less (see `PEAK_DENSITY`) and nudge this
 * only enough to cover what is left.
 */
export const LAND_SHARE = 0.56;
/** And this much of the world is the shallow shelf around it. */
export const SHALLOW_SHARE = 0.05;

/**
 * Rivers.
 *
 * A world made only of coastline is a world with one kind of edge in it. Rivers
 * put water *inside* the continent: they are shallows, so they are slow to cross
 * (half speed), expensive to take (×1.7 defence), claimable from either bank and
 * navigable by transports — which makes a river a natural border, a natural
 * choke point and a road all at once, without a single new rule.
 *
 * Each one starts high and walks downhill to the sea. The walk always takes the
 * lowest neighbour it has not already used, so it either reaches water, runs out
 * of length, or dead-ends in a highland lake — all three of which are things
 * real rivers do.
 */
export const RIVER_DENSITY = 9000;
const RIVER_MAX_LENGTH = 420;
const RIVER_MIN_LENGTH = 14;
const RIVER_SOURCE_TRIES = 60;
/** How far above the waterline the ground has to rise before it can be mountain. */
const MOUNTAIN_RISE = 0.108;

/*
 * The histogram the sea level is read off. `fbm` is normalised to [0,1), the
 * blend keeps it there, and the edge falloff can take half off it — so this
 * range brackets every possible elevation with room to spare.
 */
const ELEV_BUCKETS = 2048;
const ELEV_LO = -0.6;
const ELEV_HI = 1.1;

/**
 * @param light  A Light world grows no **ley fields**. They are the only ground
 *   a Ley Shrine may stand on and the Shrine is mana's only source, so a Light
 *   map simply has no mana in it — without changing what ley ground *is*, or
 *   any other rule. The tiles become ordinary plains, which is what they would
 *   have been if the ley noise had come out a fraction lower.
 */
export function generateMap(seed: number, worldW = MAP_W, worldH = MAP_H, light = false): WorldMap {
  const terrain = new Uint8Array(TILE_COUNT);
  const river = new Uint8Array(TILE_COUNT);
  const W = Math.max(64, Math.min(MAP_W, worldW | 0));
  const H = Math.max(64, Math.min(MAP_H, worldH | 0));

  const invW = 1 / W, invH = 1 / H;
  const f = 384 / W;

  /* ---- pass one: the elevation field, and a histogram of it ---- */
  const elev = new Float32Array(W * H);
  const hist = new Int32Array(ELEV_BUCKETS);
  const bucketScale = ELEV_BUCKETS / (ELEV_HI - ELEV_LO);

  for (let y = 0; y < H; y++) {
    const ny = y * invH;
    for (let x = 0; x < W; x++) {
      const nx = x * invW;

      /*
       * **Domain warping** — the difference between a blob and a coastline.
       *
       * The continent mask is not sampled at (x, y) but at (x, y) pushed around
       * by a second, slower noise field. Straight-ish shorelines become bays,
       * headlands, spits and the occasional inland sea, and the same seed that
       * used to produce one rounded lump now produces a landmass with a shape
       * you could recognise on a second visit. Costs two extra noise lookups
       * and nothing else.
       */
      const wx = fbm(x * 0.0042 * f, y * 0.0042 * f, seed + 1511, 2) - 0.5;
      const wy = fbm(x * 0.0042 * f, y * 0.0042 * f, seed + 2609, 2) - 0.5;
      const warp = 190 / f;
      const continent = fbm(
        (x + wx * warp) * 0.0065 * f, (y + wy * warp) * 0.0065 * f, seed + 77, 3);
      const detail = fbm(x * 0.019 * f, y * 0.019 * f, seed, 5);
      let e = continent * 0.55 + detail * 0.45;

      /*
       * The world drowns toward its edge on an **ellipse**, not a rectangle.
       *
       * A square falloff produced a square continent with rounded corners on
       * every seed — the "boring rectangle". A radial one lets the coast follow
       * the noise instead of the frame, so what reaches the edge is whatever
       * the land happened to be doing there. The radius is warped too, so even
       * the outline is not a clean oval.
       */
      const ex = nx * 2 - 1, ey = ny * 2 - 1;
      const radial = Math.sqrt(ex * ex + ey * ey) * (0.82 + (wx + wy) * 0.22);
      e -= smoothstep(0.62, 1.05, radial) * 0.62;

      const k = y * W + x;
      elev[k] = e;
      // histogram the *stored* value, so the count and the comparison below can
      // never disagree by a rounding step
      let b = ((elev[k] - ELEV_LO) * bucketScale) | 0;
      if (b < 0) b = 0; else if (b >= ELEV_BUCKETS) b = ELEV_BUCKETS - 1;
      hist[b]++;
    }
  }

  /* ---- flood it to the share we want ---- */
  const total = W * H;
  let cursor = ELEV_BUCKETS - 1;
  let acc = 0;
  const wantLand = Math.round(total * LAND_SHARE);
  while (cursor > 0 && acc + hist[cursor] <= wantLand) { acc += hist[cursor]; cursor--; }
  const seaLevel = ELEV_LO + cursor / bucketScale;
  acc = 0;
  const wantShallow = Math.round(total * SHALLOW_SHARE);
  while (cursor > 0 && acc + hist[cursor] <= wantShallow) { acc += hist[cursor]; cursor--; }
  const shelf = ELEV_LO + cursor / bucketScale;
  // a mountain is high ground relative to the coast, wherever the coast ended up
  const mountainLine = seaLevel + MOUNTAIN_RISE;

  /* ---- pass two: biomes ---- */
  for (let y = 0; y < H; y++) {
    for (let x = 0; x < W; x++) {
      const i = y * MAP_W + x;
      const e = elev[y * W + x];

      if (e < shelf) { terrain[i] = Terrain.Ocean; continue; }
      if (e < seaLevel) { terrain[i] = Terrain.Shallow; continue; }

      const ridge = fbm(x * 0.032 * f, y * 0.032 * f, seed + 313, 3);
      const moisture = fbm(x * 0.021 * f, y * 0.021 * f, seed + 991, 4);
      const ley = fbm(x * 0.055 * f, y * 0.055 * f, seed + 7777, 2);

      let t: Terrain;
      if (ley > 0.795 && !light) t = Terrain.Ley;
      else if (e > mountainLine && ridge > 0.52) t = Terrain.Mountain;
      else if (moisture > 0.545) t = Terrain.Forest;
      else t = Terrain.Plains;

      terrain[i] = t;
    }
  }

  raisePeaks(terrain, W, H);
  carvePasses(terrain, W, H, seed);
  carveRivers(terrain, river, elev, W, H, seed);

  // outside the world is left as Terrain.Ocean (0) — the array starts zeroed,
  // and ocean is neither walkable nor claimable, so the edge needs no other guard
  return {
    seed, terrain, river,
    landTiles: countClaimable(terrain, river, W, H),
    worldW: W, worldH: H,
  };
}

/**
 * How much of a 5×5 neighbourhood has to be mountain before its middle becomes
 * an impassable peak. High, on purpose: only the deep interior of a real range
 * seals, so every range keeps a walkable skirt and a lone crag is still a crag.
 */
/*
 * 0.96, not 0.86.
 *
 * The looser figure sealed something like a *tenth of the world*, which is not
 * a mountain range, it is a mountain continent — and since a peak is nobody's
 * ground it came straight out of the land players were promised. At 24 of 25
 * only the genuine interior of a broad massif closes, so ranges read as walls
 * across a landscape rather than as the landscape.
 */
const PEAK_DENSITY = 0.96;
/** A range thinner than this across never seals at all. */
const PEAK_MIN_RUN = 3;
/**
 * The most of a world that may ever be impassable peak.
 *
 * Every sealed tile is land nobody can claim, so this comes directly out of the
 * `LAND_SHARE` promise. Fixed rather than emergent, because how much mountain a
 * seed rolls is not fixed at all.
 */
const PEAK_MAX_SHARE = 0.03;

/**
 * Raises the spine of every thick mountain mass into impassable High Peaks.
 *
 * Measured rather than noised, so it follows the range the biome pass actually
 * produced instead of inventing a second, differently-shaped one on top of it.
 * A tile seals only when it is buried inside mountain on every side — which
 * leaves the fringe of every range walkable, so ranges *narrow* the map without
 * cutting it up, and the sealed parts read as the high country they are.
 */
function raisePeaks(terrain: Uint8Array, W: number, H: number) {
  const sealed: number[] = [];
  const buried: number[] = [];
  for (let y = PEAK_MIN_RUN; y < H - PEAK_MIN_RUN; y++) {
    for (let x = PEAK_MIN_RUN; x < W - PEAK_MIN_RUN; x++) {
      const i = y * MAP_W + x;
      if (terrain[i] !== Terrain.Mountain) continue;
      let rock = 0, seen = 0;
      for (let dy = -2; dy <= 2; dy++) {
        for (let dx = -2; dx <= 2; dx++) {
          seen++;
          if (terrain[(y + dy) * MAP_W + (x + dx)] === Terrain.Mountain) rock++;
        }
      }
      if (rock / seen >= PEAK_DENSITY) { sealed.push(i); buried.push(rock); }
    }
  }

  /*
   * **A hard ceiling on how much of the world can be sealed.**
   *
   * Tuning the density threshold alone was not enough and never could be: how
   * much mountain a seed produces varies, so the same threshold seals 3 % of
   * one world and 14 % of the next — and every point of that comes straight out
   * of the claimable land players were promised. Sorting by how deeply buried
   * each candidate is and taking only the top slice makes the cost of ranges a
   * fixed, known quantity on every seed, and keeps the parts that are most
   * obviously the interior of a massif.
   *
   * Ties break on tile index so the result is identical on client and server,
   * which it has to be — the map is never sent, only the seed.
   */
  const cap = Math.floor(W * H * PEAK_MAX_SHARE);
  const order = sealed.map((_, k) => k);
  order.sort((a, b) => (buried[b] - buried[a]) || (sealed[a] - sealed[b]));
  const take = Math.min(cap, order.length);

  // applied after the scan, or each new peak would stop counting as mountain
  // for its neighbours and the range would seal only along one diagonal
  for (let k = 0; k < take; k++) terrain[sealed[order[k]]] = Terrain.Peak;
}

/**
 * **Cuts a pass through every wall that would otherwise seal a region off.**
 *
 * Flood-fills the walkable ground, finds the biggest connected piece, and then
 * — for every other piece worth reaching — digs the shortest corridor of
 * ordinary Mountain from it back to the main body.
 *
 * That order matters: the passes are not decoration sprinkled over the range,
 * they are *the reason the map is connected at all*. Every one of them is
 * therefore a genuine chokepoint by construction — the only road between two
 * halves of a continent, three tiles wide, on ×2.1 defence ground. A player who
 * holds one holds everything behind it, and there is no way round because if
 * there were, the pass would never have been cut.
 *
 * Regions too small to matter are left sealed. An unreachable four-tile ledge
 * is scenery; punching a corridor to it would only add a pass that leads
 * nowhere.
 */
const PASS_MIN_REGION = 60;
const PASS_HALF_WIDTH = 1;

function carvePasses(terrain: Uint8Array, W: number, H: number, seed: number) {
  const rand = mulberry32((seed ^ 0x9a55) >>> 0);
  const region = new Int32Array(W * H).fill(-1);
  const regions: number[][] = [];
  const stack: number[] = [];

  const walkable = (x: number, y: number) =>
    x >= 0 && y >= 0 && x < W && y < H && WALKABLE[terrain[y * MAP_W + x]];

  for (let y = 0; y < H; y++) {
    for (let x = 0; x < W; x++) {
      if (!walkable(x, y) || region[y * W + x] >= 0) continue;
      const id = regions.length;
      const cells: number[] = [];
      stack.length = 0;
      stack.push(y * W + x);
      region[y * W + x] = id;
      while (stack.length) {
        const k = stack.pop()!;
        const cx = k % W, cy = (k / W) | 0;
        cells.push(k);
        for (let d = 0; d < 4; d++) {
          const nx = cx + (d === 0 ? 1 : d === 1 ? -1 : 0);
          const ny = cy + (d === 2 ? 1 : d === 3 ? -1 : 0);
          if (!walkable(nx, ny)) continue;
          const nk = ny * W + nx;
          if (region[nk] >= 0) continue;
          region[nk] = id;
          stack.push(nk);
        }
      }
      regions.push(cells);
    }
  }
  if (regions.length < 2) return;

  let main = 0;
  for (let i = 1; i < regions.length; i++) if (regions[i].length > regions[main].length) main = i;

  for (let i = 0; i < regions.length; i++) {
    if (i === main || regions[i].length < PASS_MIN_REGION) continue;

    // the closest pair of cells between this island of land and the mainland,
    // sampled rather than exhaustive — an exact answer costs O(n·m) and a good
    // one costs nothing worth measuring
    let bestA = -1, bestB = -1, bestD = Infinity;
    const mine = regions[i], big = regions[main];
    const stepA = Math.max(1, Math.floor(mine.length / 240));
    const stepB = Math.max(1, Math.floor(big.length / 240));
    for (let a = 0; a < mine.length; a += stepA) {
      const ax = mine[a] % W, ay = (mine[a] / W) | 0;
      for (let b = 0; b < big.length; b += stepB) {
        const bx = big[b] % W, by = (big[b] / W) | 0;
        const d = (ax - bx) ** 2 + (ay - by) ** 2;
        if (d < bestD) { bestD = d; bestA = mine[a]; bestB = big[b]; }
      }
    }
    if (bestA < 0 || bestB < 0) continue;

    // and dig it: a straight corridor, wobbled a little so a pass reads as a
    // valley rather than a tunnel bored by a machine
    let cx = bestA % W, cy = (bestA / W) | 0;
    const tx = bestB % W, ty = (bestB / W) | 0;
    for (let step = 0; step < 4096; step++) {
      for (let dy = -PASS_HALF_WIDTH; dy <= PASS_HALF_WIDTH; dy++) {
        for (let dx = -PASS_HALF_WIDTH; dx <= PASS_HALF_WIDTH; dx++) {
          const x = cx + dx, y = cy + dy;
          if (x < 1 || y < 1 || x >= W - 1 || y >= H - 1) continue;
          const k = y * MAP_W + x;
          if (terrain[k] === Terrain.Peak) terrain[k] = Terrain.Mountain;
        }
      }
      if (cx === tx && cy === ty) break;
      const dx = tx - cx, dy = ty - cy;
      // step along the dominant axis, with an occasional sideways drift
      if (Math.abs(dx) > Math.abs(dy)) {
        cx += Math.sign(dx);
        if (rand() < 0.22 && dy !== 0) cy += Math.sign(dy);
      } else {
        cy += Math.sign(dy);
        if (rand() < 0.22 && dx !== 0) cx += Math.sign(dx);
      }
    }
  }
}

const RIVER_DX = [1, -1, 0, 0, 1, 1, -1, -1];
const RIVER_DY = [0, 0, 1, -1, 1, -1, 1, -1];

/**
 * **How wide a river runs: two tiles, not one.**
 *
 * A one-tile stream is a ford. You wade it, you can see both banks from the
 * middle of it, and a hull has nowhere to *be* — which is how a river ended up
 * being the one border in the game nobody could cross. A player with a stream
 * between him and his neighbour could not launch a boat onto it, could not
 * sail along it, and so could not take a single tile of the far bank. At two
 * tiles a channel is a waterway: water to launch into, water to sail along,
 * and a far bank to land on.
 *
 * The brush is a 2x2 block with its corner on the course tile, rather than a
 * plus, and that is not a cosmetic choice. Two 2x2 blocks stamped on any two
 * of the eight neighbouring tiles always overlap or share an edge, so a course
 * carved with this one is **orthogonally connected by construction** — which
 * every 4-neighbour water search in the server quietly depends on
 * (`Game.seaComponents`, and the flood fills in the checks). A plus brush would
 * be three tiles across and cost half again as much land for the same property.
 */
const RIVER_BRUSH = 2;

/**
 * The most of a world that may ever be river.
 *
 * A river tile is nobody's ground — `countClaimable` excludes it — so every one
 * of them is spent straight out of the promise `LAND_SHARE` makes, exactly as a
 * High Peak is. It is capped here for the same reason and in the same shape as
 * `PEAK_MAX_SHARE`. Widening the channels is what made it matter: a two-tile
 * waterway costs a little over twice what the old scratch did, and *how much*
 * river a seed rolls was never a fixed quantity to begin with.
 */
const RIVER_MAX_SHARE = 0.022;
/**
 * …and the claimable share the carve refuses to drop a world below.
 *
 * The cap above is a share of the *world*; this is a floor under what is left
 * **claimable**, measured on the continent this seed actually produced rather
 * than assumed from an average. A seed that already spent a lot of its land on
 * High Peaks therefore gets fewer rivers automatically, instead of a budget
 * that holds on eleven seeds and drowns the twelfth.
 *
 * It sits above the 0.5 the promise is stated at because the estimate the carve
 * spends against is per-tile, and cannot see the second-order case where
 * drowning a beach costs a coastal shallow behind it its only land neighbour.
 * That gap is the margin.
 */
const RIVER_LAND_FLOOR = 0.52;

/**
 * Cuts rivers into a finished continent.
 *
 * Sources are the highest of a handful of random land tiles — a spring belongs
 * in the hills — and each river then walks to the **lowest neighbour it has not
 * already visited**, which descends where there is anywhere to descend to and
 * wanders sideways where there is not, rather than stopping dead in the first
 * dimple in the noise. Too short a channel is discarded: a four-tile puddle is
 * not a river, it is a hole in the map.
 *
 * The course is then *stamped* rather than merely coloured in — see
 * `RIVER_BRUSH` for the width and `RIVER_MAX_SHARE` for what the world is
 * willing to pay for it.
 */
function carveRivers(
  terrain: Uint8Array, river: Uint8Array, elev: Float32Array, W: number, H: number, seed: number,
) {
  const rand = mulberry32((seed ^ 0x5eed1e57) >>> 0);
  const count = Math.max(6, Math.round((W * H) / RIVER_DENSITY));
  const path: number[] = [];
  /**
   * The tiles that keep a **diagonal** step orthogonally connected.
   *
   * Half the headings above are diagonals, so a river can and does step from one
   * tile to another it shares nothing with but a point. Every 4-neighbour water
   * search then sees two separate waterways and refuses to route between them,
   * and a hull may not cut the corner either (see `moveTo` on the server) —
   * which is precisely how a river became a wall. So each diagonal step also
   * wets the shallower of the two tiles it squeezes between.
   *
   * The brush below usually covers this on its own. This does not exist for the
   * usual case: it is the guarantee that still holds at a river mouth, and where
   * a channel is squeezed past a peak, which are exactly the places the brush
   * cannot widen and a one-tile channel therefore survives.
   */
  const links: number[] = [];
  const seen = new Set<number>();
  /** Every tile one river would wet: its course, its links and its brush. */
  const wet = new Set<number>();
  /** Which cells of the brush block actually went in — see the stamp below. */
  const stamped = new Array<boolean>(RIVER_BRUSH * RIVER_BRUSH).fill(false);

  /*
   * What the whole world's rivers may cost, in ground nobody will be able to
   * claim. Whichever of the two limits bites first wins. Measured once, here,
   * because `raisePeaks` and `carvePasses` have both already had their say by
   * the time this runs and the land left over is what there is to spend.
   */
  const claimable = countClaimable(terrain, river, W, H);
  const budget = Math.max(0, Math.min(
    Math.floor(W * H * RIVER_MAX_SHARE),
    claimable - Math.ceil(W * H * RIVER_LAND_FLOOR),
  ));
  let spent = 0;

  /**
   * Wets one tile of the brush, and reports whether it counts as water now.
   *
   * A tile that is already sea or shelf is left exactly as it was: it is
   * already sailable, and re-labelling a beach as river would quietly take a
   * claimable coastal tile away for nothing. A High Peak is refused outright —
   * the brush widens a river through country, it does not dissolve a range.
   */
  const brush = (x: number, y: number): boolean => {
    if (x < 1 || y < 1 || x >= W - 1 || y >= H - 1) return false;
    const k = y * MAP_W + x;
    if (terrain[k] === Terrain.Peak) return false;
    wet.add(k);
    return true;
  };

  for (let r = 0; r < count; r++) {
    if (spent >= budget) break;

    let sx = -1, sy = -1, highest = -Infinity;
    for (let a = 0; a < RIVER_SOURCE_TRIES; a++) {
      const x = 3 + Math.floor(rand() * (W - 6));
      const y = 3 + Math.floor(rand() * (H - 6));
      if (terrain[y * MAP_W + x] < Terrain.Plains) continue;
      const e = elev[y * W + x];
      if (e > highest) { highest = e; sx = x; sy = y; }
    }
    if (sx < 0) continue;

    path.length = 0;
    links.length = 0;
    seen.clear();
    let cx = sx, cy = sy;
    for (let step = 0; step < RIVER_MAX_LENGTH; step++) {
      const i = cy * MAP_W + cx;
      if (terrain[i] < Terrain.Plains) break;      // it has reached water
      if (seen.has(i)) break;
      seen.add(i);
      path.push(i);

      let nx = -1, ny = -1, low = Infinity;
      for (let d = 0; d < 8; d++) {
        const x = cx + RIVER_DX[d], y = cy + RIVER_DY[d];
        if (x < 1 || y < 1 || x >= W - 1 || y >= H - 1) continue;
        if (seen.has(y * MAP_W + x)) continue;
        const e = elev[y * W + x];
        if (e < low) { low = e; nx = x; ny = y; }
      }
      if (nx < 0) break;

      /*
       * Recorded before the step is taken, and therefore recorded for the last
       * step too — the one that ends the walk by reaching the sea. That step is
       * the river mouth, and a mouth joined to the sea by nothing but a corner
       * is a river no ship can leave.
       */
      if (nx !== cx && ny !== cy) {
        // the water goes round the low side of the corner, as water does
        links.push(elev[cy * W + nx] <= elev[ny * W + cx] ? cy * MAP_W + nx : ny * MAP_W + cx);
      }

      cx = nx; cy = ny;
    }

    if (path.length < RIVER_MIN_LENGTH) continue;

    wet.clear();
    for (const i of path) {
      const x = i % MAP_W, y = (i / MAP_W) | 0;
      // the course itself is carved through anything, peaks included, exactly as
      // it always was — a river that has cut a gorge has cut it
      wet.add(i);
      stamped[0] = true;
      for (let by = 0; by < RIVER_BRUSH; by++) {
        for (let bx = 0; bx < RIVER_BRUSH; bx++) {
          if (bx === 0 && by === 0) continue;
          /*
           * A cell of the block only goes in if the cell above it or to its left
           * did. Where a peak refused both, this one would be joined to the
           * course by nothing but a point — the exact shape the whole change
           * exists to stop producing — so it is refused too.
           */
          const reach = (bx > 0 && stamped[by * RIVER_BRUSH + bx - 1])
            || (by > 0 && stamped[(by - 1) * RIVER_BRUSH + bx]);
          stamped[by * RIVER_BRUSH + bx] = reach && brush(x + bx, y + by);
        }
      }
    }
    // links are not negotiable; the brush is
    for (const i of links) wet.add(i);

    /*
     * What this river costs is what it drowns. Sea, shelf and peak are already
     * unclaimable or already wet, so they are free — which is why a river that
     * runs the last of its length along an existing coast is nearly so.
     */
    let cost = 0;
    for (const i of wet) if (USABLE_LAND[terrain[i]]) cost++;
    if (spent + cost > budget) continue;          // this one is more than the world can spare
    spent += cost;

    for (const i of wet) {
      if (terrain[i] < Terrain.Plains) continue;  // already water, and already sailable
      terrain[i] = Terrain.Shallow;
      river[i] = 1;
    }
  }
}

/**
 * How much ground can actually be painted: real land, plus the shallows that
 * touch it. Open shallow water can never be claimed by anyone, so counting it
 * as "land" would quietly put the 95 % victory line out of everybody's reach.
 */
function countClaimable(terrain: Uint8Array, river: Uint8Array, W: number, H: number): number {
  let n = 0;
  for (let y = 0; y < H; y++) {
    for (let x = 0; x < W; x++) {
      const i = y * MAP_W + x;
      const t = terrain[i];
      // High Peaks are land, but nobody's land — counting them would push the
      // 95 % victory line above what the map can actually be painted with
      if (USABLE_LAND[t]) { n++; continue; }
      if (t !== Terrain.Shallow || river[i]) continue;   // a river belongs to nobody
      if ((x > 0 && USABLE_LAND[terrain[i - 1]])
        || (x < W - 1 && USABLE_LAND[terrain[i + 1]])
        || (y > 0 && USABLE_LAND[terrain[i - MAP_W]])
        || (y < H - 1 && USABLE_LAND[terrain[i + MAP_W]])) n++;
    }
  }
  return n;
}

/**
 * Per-tile art variation, deterministic and identical everywhere.
 * Used by the renderer to pick a 4x4 pixel motif variant.
 */
export const tileVariant = (x: number, y: number, seed: number) => hash2(x, y, seed + 4242);

/** True when the tile is water of any kind. */
export const isWater = (t: number) => t === Terrain.Ocean || t === Terrain.Shallow;
